import axios from "axios"
import { useCallback, useEffect, useState } from "react"
import { useStoreState } from "easy-peasy"
import ErrorPopup from "./ErrorPopup.js"
import StatusPopup from "./StatusPopup.js"
import {
	DEDUP_CANDIDATES_URL,
	DEDUP_MERGE_URL,
	DEDUP_UNMERGE_URL,
	DEDUP_NOT_DUPLICATE_URL,
	DEDUP_HISTORY_URL,
} from "./Constants"

// Tiers come from dedup_scan.py. 'auto' pairs have usually been merged already by
// the overnight run; any left here are ones it declined to touch.
const TIERS = [
	{ value: "", label: "All" },
	{ value: "review", label: "Needs a decision" },
	{ value: "double_charge", label: "Possible double payment" },
	{ value: "auto", label: "Clear duplicates" },
]

const TIER_COLOR = { review: "#e67e22", double_charge: "#c0392b", auto: "#27ae60" }

const th = { textAlign: "left", whiteSpace: "nowrap" }

function addressOf(p) {
	const line = [p.number_addr1, p.street_addr1].filter(Boolean).join(" ")
	const town = [p.city_name, p.state_code, p.zip].filter(Boolean).join(" ")
	return [line, town].filter(Boolean).join(", ") || "—"
}

function activityOf(p) {
	const bits = []
	if (p.dog_count) bits.push(`${p.dog_count} dog${p.dog_count === 1 ? "" : "s"}`)
	if (p.transaction_count) bits.push(`${p.transaction_count} payment${p.transaction_count === 1 ? "" : "s"}`)
	return bits.length ? bits.join(", ") : "nothing on file"
}

// One side of a proposed pair.
function Side({ person, role }) {
	return (
		<div style={{ flex: 1, minWidth: "14rem" }}>
			{/* No record id: deputies identify people by name and address, not by
			    the internal taxpayer id, which means nothing to them. */}
			<div style={{ fontSize: "0.8rem", textTransform: "uppercase", opacity: 0.7 }}>
				{role}
			</div>
			<div style={{ fontWeight: "bold" }}>
				{person.first_name} {person.last_name}
			</div>
			<div>{addressOf(person)}</div>
			<div style={{ fontSize: "0.9rem", opacity: 0.85 }}>{activityOf(person)}</div>
		</div>
	)
}

function DedupReview() {
	const Token = useStoreState((state) => state.Token)
	const [tier, setTier] = useState("review")
	const [candidates, setCandidates] = useState([])
	const [history, setHistory] = useState([])
	const [status, setStatus] = useState("")
	const [error, setError] = useState("")
	const [busyId, setBusyId] = useState(null)

	const auth = useCallback(
		() => ({ login_id: Token.login_id, client_id: Token.client_id }),
		[Token.login_id, Token.client_id]
	)

	const load = useCallback(async () => {
		setStatus("loading")
		try {
			const [{ data: cands }, { data: hist }] = await Promise.all([
				axios.post(DEDUP_CANDIDATES_URL, { ...auth(), tier }),
				axios.post(DEDUP_HISTORY_URL, { ...auth(), limit: 25 }),
			])
			setCandidates(cands)
			setHistory(hist)
			setStatus(cands.length ? "" : "empty")
		} catch (err) {
			setStatus("")
			setError(err.response?.data?.detail || err.message)
		}
	}, [auth, tier])

	useEffect(() => {
		load()
	}, [load])

	async function act(url, body, candidateId) {
		setBusyId(candidateId)
		setError("")
		try {
			await axios.post(url, { ...auth(), ...body })
			await load()
		} catch (err) {
			setError(err.response?.data?.detail || err.message)
		} finally {
			setBusyId(null)
		}
	}

	return (
		/* className="Main" is required, not cosmetic: .base-theme is height:100vh
		   with overflow:hidden, so the page itself never scrolls — .Main is the
		   scroll container. A plain div here clips at the footer. */
		<main className="Main" style={{ padding: "1rem" }}>
			<h2>Duplicate Taxpayer Review</h2>
			<p style={{ maxWidth: "48rem", lineHeight: "1.5" }}>
				Records that look like the same person entered twice. Combining moves every
				dog, tag and payment onto the record you keep — nothing is discarded, and
				any combine can be undone below.
			</p>

			<fieldset>
				<legend>Show</legend>
				{TIERS.map((t) => (
					<label key={t.value} style={{ marginRight: "1.25rem" }}>
						<input
							type="radio"
							name="tier"
							checked={tier === t.value}
							onChange={() => setTier(t.value)}
						/>{" "}
						{t.label}
					</label>
				))}
			</fieldset>

			{/* Side by side so the queue and the undo list are visible at once.
			    flex-wrap drops them back to stacked columns on a narrow screen. */}
			<div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", flexWrap: "wrap" }}>
			<fieldset style={{ flex: "1 1 34rem", minWidth: 0 }}>
				<legend>Pending ({candidates.length})</legend>
				{candidates.length === 0 && <p>Nothing waiting for review.</p>}
				{/* Scrolls within the panel so the tier filter above and the combine
				    history below stay put no matter how long the queue gets. */}
				<div style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: "0.5rem" }}>
				{candidates.map((c) => (
					<div
						key={c.candidate_id}
						style={{
							borderLeft: `4px solid ${TIER_COLOR[c.tier] || "#888"}`,
							padding: "0.6rem 0.9rem",
							marginBottom: "0.75rem",
							background: "rgba(128,128,128,0.08)",
						}}
					>
						<div style={{ fontSize: "0.85rem", color: TIER_COLOR[c.tier], marginBottom: "0.4rem" }}>
							{c.reason}
						</div>
						{/* Newest/older is decided by the taxpayer id, which is assigned in
						    creation order — the keeper is not always the newer of the two. */}
						<div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
							<Side
								person={c.keeper}
								role={`${c.keeper.id > c.loser.id ? "Newest" : "Older"} record — kept`}
							/>
							<Side
								person={c.loser}
								role={`${c.keeper.id > c.loser.id ? "Older" : "Newest"} record — combined in`}
							/>
						</div>
						<div style={{ marginTop: "0.6rem", display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
							<button
								className="saveBtn"
								type="button"
								disabled={busyId === c.candidate_id}
								onClick={() =>
									act(DEDUP_MERGE_URL,
										{ keeper_id: c.keeper.id, loser_id: c.loser.id, reason: c.reason },
										c.candidate_id)
								}
							>
								Combine these
							</button>
							<button
								className="saveBtn"
								type="button"
								disabled={busyId === c.candidate_id}
								onClick={() =>
									act(DEDUP_NOT_DUPLICATE_URL,
										{ id_a: c.keeper.id, id_b: c.loser.id, note: "reviewed in queue" },
										c.candidate_id)
								}
							>
								Different people
							</button>
						</div>
					</div>
				))}
				</div>
			</fieldset>

			<fieldset style={{ flex: "1 1 24rem", minWidth: 0 }}>
				<legend>Recent combines</legend>
				{history.length === 0 && <p>No records have been combined yet.</p>}
				{history.length > 0 && (
					<table className="receiptsts-table">
						<thead>
							<tr>
								<th style={th}>When</th>
								<th style={th}>Record combined in</th>
								<th style={th}>By</th>
								<th style={th}></th>
							</tr>
						</thead>
						<tbody>
							{history.map((h) => (
								<tr key={h.journal_id}>
									<td>{h.merged_at ? h.merged_at.replace("T", " ").slice(0, 16) : "—"}</td>
									<td>
										{h.loser_name}
										<br />
										<span style={{ opacity: 0.75 }}>{h.loser_address}</span>
									</td>
									<td>{h.source === "auto" ? "Overnight" : "Staff"}</td>
									<td>
										{h.reversed_at ? (
											<span style={{ opacity: 0.7 }}>Undone</span>
										) : (
											<button
												className="saveBtn"
												type="button"
												disabled={busyId === h.journal_id}
												onClick={() =>
													act(DEDUP_UNMERGE_URL, { journal_id: h.journal_id }, h.journal_id)
												}
											>
												Undo
											</button>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</fieldset>
			</div>

			{status === "loading" && <StatusPopup message="Loading..." />}
			<ErrorPopup message={error} onClose={() => setError("")} />
		</main>
	)
}

export default DedupReview
